import { Select, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { TFeatureWorkflowInstance } from "entity-app/models";

import {
  COMPUTE_STRATEGY_LABELS,
  ErrorMessages,
  ProjectPermissionEnum,
  SuccessMessages
} from "constants/index";
import { useContext } from "react";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import ComputeStrategyTag from "components/manage-feature-wf-templates/widgets/compute-strategy-tag";
import { ComputeStrategy } from "components/workflow-template-editor/model";
import useWorkflowTemplateHook from "./use-workflow-template-hook";

const keyLoadingMessage = "loadingMessagekey";

function ProjectWorkflowTemplateDropdown(props: {
  featureId: number;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  afterChange?: () => void;
  className?: string;
  setApiCallLoading?: (isLoading: boolean) => void;
  disable?: boolean;
  disableTooltip?: string;
}) {
  const {
    featureId,
    featureWorkflowInstance,
    afterChange,
    setApiCallLoading,
    className = "",
    disable = false,
    disableTooltip
  } = props;
  const { tokenContents } = useContext(ProjectContext);
  const cannotChangeTemplate = isPermissionNotGrantted(
    ProjectPermissionEnum.EditWorkflowTemplate,
    tokenContents?.role ?? ""
  );

  const {
    options,
    loading,
    selectedMaterialTemplateId,
    setSelectedMaterialTemplateId,
    applyingMDBTemplate,
    isWFStarted,
    applyWorkflowTemplateMutation
  } = useWorkflowTemplateHook({ featureId, featureWorkflowInstance });

  const onTemplateSelectChange = async (templateId: string) => {
    message.loading({
      content: "Recalculating...",
      key: keyLoadingMessage,
      duration: 60000
    });
    setApiCallLoading?.(true);
    setSelectedMaterialTemplateId(templateId);

    const input = [
      {
        feature_instance_id: featureWorkflowInstance.feature_instance_id,
        workflow_instance_id: featureWorkflowInstance.id,
        workflow_template_id: templateId
      }
    ];

    const res = await applyWorkflowTemplateMutation({
      variables: {
        input,
        metadata: {
          request_source: "DETAIL_PAGE"
        }
      }
    });
    if (res.data) {
      message.success({
        content: SuccessMessages.DBTemplateAppliedSuccess,
        key: keyLoadingMessage,
        duration: 3
      });
      if (afterChange) afterChange();
    } else {
      message.error({
        content: ErrorMessages.SomethingWentWrong,
        key: keyLoadingMessage,
        duration: 3
      });
    }
    setApiCallLoading?.(false);
  };

  const getStrategyHintText = (option: {
    dateblock_compute_strategy?: {
      key: ComputeStrategy;
      dateblockHint?: string;
    };
  }) =>
    // getStrategyHintText runs only when COMPUTE_STRATEGY_LABELS[key] exists, and
    // every entry defines a dateblockHint string, so the `?? undefined` arm is dead
    /* v8 ignore next 3 */
    COMPUTE_STRATEGY_LABELS[
      option.dateblock_compute_strategy?.key as ComputeStrategy
    ]?.dateblockHint ?? undefined;

  const templateSelect = (
    <Select
      className={className}
      style={{ minWidth: 160 }}
      value={options.length ? selectedMaterialTemplateId : null}
      onChange={onTemplateSelectChange}
      disabled={
        cannotChangeTemplate || isWFStarted || applyingMDBTemplate || disable
      }
      loading={applyingMDBTemplate || loading}
      placeholder="Select a template"
      data-testid="index-select-template"
      optionLabelProp="label"
    >
      {options.map((option) => (
        <Select.Option
          key={option.value}
          value={option.value}
          label={
            <span className="inline-flex items-center gap-2 w-full">
              <span className="truncate">{option.label}</span>
              {option.dateblock_compute_strategy?.name &&
                COMPUTE_STRATEGY_LABELS[
                  option.dateblock_compute_strategy.key as ComputeStrategy
                ] && (
                  <span className="shrink-0">
                    <ComputeStrategyTag
                      label={
                        COMPUTE_STRATEGY_LABELS[
                          option.dateblock_compute_strategy
                            .key as ComputeStrategy
                        ]?.label
                      }
                      hintText={getStrategyHintText(option)}
                      size="small"
                    />
                  </span>
                )}
            </span>
          }
        >
          <span className="inline-flex items-center gap-2 w-full">
            <span className="truncate">{option.label}</span>
            {option.dateblock_compute_strategy?.name &&
              COMPUTE_STRATEGY_LABELS[
                option.dateblock_compute_strategy.key as ComputeStrategy
              ] && (
                <span className="shrink-0">
                  <ComputeStrategyTag
                    label={
                      COMPUTE_STRATEGY_LABELS[
                        option.dateblock_compute_strategy.key as ComputeStrategy
                      ]?.label
                    }
                    size="small"
                  />
                </span>
              )}
          </span>
        </Select.Option>
      ))}
    </Select>
  );

  if (!disableTooltip || isWFStarted) return templateSelect;

  return <Tooltip title={disableTooltip}>{templateSelect}</Tooltip>;
}

export default ProjectWorkflowTemplateDropdown;
